import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Briefcase } from 'lucide-react';

/**
 * Compact "filter by client" pill used across PostsPage, CalendarPage, ArchivePage,
 * and AIPostManagerPage. Renders nothing until the parent has loaded `clients`.
 *
 * Props:
 *   value: current client_id (or 'all')
 *   onChange: (clientId | 'all') => void
 *   clients: array from useClients()
 *   includeAll: bool (default true) — show the "All clients" option
 *   placeholder: optional override for the placeholder text
 *   width: tailwind width utility (default 'w-[160px]')
 */
export const ClientFilterPill = ({
  value,
  onChange,
  clients,
  includeAll = true,
  placeholder = 'All clients',
  width = 'w-[170px]',
}) => {
  if (!clients || clients.length === 0) return null;
  return (
    <Select value={value || 'all'} onValueChange={onChange}>
      <SelectTrigger className={width} data-testid="client-filter-pill">
        <Briefcase className="w-3.5 h-3.5 text-zinc-500 mr-1" />
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {includeAll && (
          <SelectItem value="all" data-testid="client-filter-all">{placeholder}</SelectItem>
        )}
        {clients.map((c) => (
          <SelectItem key={c.id} value={c.id} data-testid={`client-filter-option-${c.id}`}>
            <span className="flex items-center gap-2">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ background: c.brand_color || '#6366f1' }}
              />
              <span className="truncate">{c.name}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

/**
 * Tiny inline badge shown on each post card so the user instantly sees which
 * client it belongs to. No-op when the post has no client_id.
 */
export const ClientBadge = ({ clientId, clientsById, className = '' }) => {
  if (!clientId) return null;
  const c = clientsById?.[clientId];
  if (!c) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${className}`}
      style={{ background: `${c.brand_color}1a`, color: c.brand_color }}
      data-testid={`client-badge-${clientId}`}
      title={`Client: ${c.name}`}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: c.brand_color }}
      />
      {c.name}
    </span>
  );
};
